<template>
  <v-card
    class="heatmap-card"
    elevation="1"
    rounded="lg"
    :loading="loading"
  >
    <!-- ── Header ── -->
    <div class="heatmap-header">
      <div class="header-left">
        <v-icon
          size="15"
          class="header-icon"
        >
          mdi-grid
        </v-icon>
        <span class="header-title">Network Health Heatmap</span>
      </div>
      <div
        v-if="hasData"
        class="legend-row"
      >
        <div
          v-for="leg in legend"
          :key="leg.label"
          class="legend-item"
        >
          <span
            class="legend-swatch"
            :style="{ background: leg.color }"
          />
          <span class="legend-label">{{ leg.label }}</span>
        </div>
      </div>
    </div>

    <!-- ── ApexChart Heatmap ── -->
    <div
      v-if="hasData"
      class="chart-wrap"
    >
      <apexchart
        type="heatmap"
        :height="chartHeight"
        :options="chartOptions"
        :series="series"
      />
    </div>

    <!-- ── Empty state ── -->
    <div
      v-else
      class="empty-state"
    >
      <v-icon
        size="30"
        color="grey lighten-1"
      >
        mdi-grid-off
      </v-icon>
      <div class="empty-text">
        No records found
      </div>
      <div class="empty-subtext">
        No location activity data available for this period
      </div>
    </div>
  </v-card>
</template>

<script>
import VueApexCharts from "vue-apexcharts";

export default {
  name: "NetworkHealthHeatmap",
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    loading: {
      type: Boolean,
      default() {
        return false;
      },
    },
    /**
     * The `locations` array from the API:
     * [{ location_id, location_name, heatmap: [{ hour, session_count, energy_consumed, total_cost }] }]
     */
    items: {
      type: Array,
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      legend: [
        { label: "No Activity", color: "#e5e7eb" },
        { label: "Low", color: "#99f6e4" },
        { label: "Medium", color: "#2dd4bf" },
        { label: "High", color: "#0d9488" },
      ],
    };
  },

  computed: {
    hasData() {
      return Array.isArray(this.items) && this.items.length > 0;
    },

    /** One heatmap row per location; y = sessions in that hour */
    series() {
      return this.items.map((location) => ({
        name: location.location_name,
        data: (location.heatmap || []).map((cell) => ({
          x: this.formatHour(cell.hour),
          y: cell.session_count || 0,
          // extra fields for the custom tooltip
          energy: cell.energy_consumed || 0,
          cost: cell.total_cost || 0,
        })),
      }));
    },

    /** Scale card height with the number of location rows */
    chartHeight() {
      return Math.max(this.items.length * 42 + 60, 140);
    },

    chartOptions() {
      return {
        chart: {
          type: "heatmap",
          toolbar: { show: false },
          animations: { enabled: true, speed: 500 },
          background: "transparent",
        },
        dataLabels: { enabled: false },
        stroke: {
          width: 2,
          colors: ["#ffffff"],
        },
        plotOptions: {
          heatmap: {
            radius: 3,
            enableShades: false,
            colorScale: {
              ranges: [
                { from: 0, to: 0, name: "No Activity", color: "#e5e7eb" },
                { from: 1, to: 2, name: "Low", color: "#99f6e4" },
                { from: 3, to: 5, name: "Medium", color: "#2dd4bf" },
                { from: 6, to: 10000, name: "High", color: "#0d9488" },
              ],
            },
          },
        },
        xaxis: {
          type: "category",
          labels: {
            rotate: 0,
            style: {
              fontFamily: "DM Mono, monospace",
              fontSize: "9px",
              colors: "#9aa5b4",
            },
            // show only every 4th hour to keep the axis clean
            formatter: (val) => {
              const hour = parseInt(val, 10);
              return hour % 4 === 0 ? val : "";
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        },
        yaxis: {
          labels: {
            style: {
              fontFamily: "DM Mono, monospace",
              fontSize: "10px",
              colors: "#9aa5b4",
            },
            maxWidth: 160,
          },
        },
        grid: { show: false },
        legend: { show: false },
        tooltip: {
          theme: "light",
          style: { fontFamily: "Sora, sans-serif", fontSize: "11px" },
          custom: ({ seriesIndex, dataPointIndex, w }) => {
            const point =
              w.config.series[seriesIndex].data[dataPointIndex] || {};
            const location = w.config.series[seriesIndex].name;

            return `
              <div class="heatmap-tooltip">
                <div class="tt-title">${location} — ${point.x}</div>
                <div class="tt-row">Sessions: <b>${point.y ?? 0}</b></div>
                <div class="tt-row">Energy: <b>${(point.energy ?? 0).toFixed(
                  2
                )} kWh</b></div>
                <div class="tt-row">Cost: <b>PKR ${(
                  point.cost ?? 0
                ).toLocaleString()}</b></div>
              </div>
            `;
          },
        },
      };
    },
  },

  methods: {
    // 0 -> "00:00", 13 -> "13:00"
    formatHour(hour) {
      return `${String(hour).padStart(2, "0")}:00`;
    },
  },
};
</script>

<style scoped>
.heatmap-card {
  background: #ffffff !important;
  border: 1px solid #e8ecf0 !important;
  overflow: hidden;
}

/* ── Header ── */
.heatmap-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 14px 20px 10px;
  border-bottom: 1px solid #f0f2f5;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 7px;
}

.header-icon {
  color: #4a5568 !important;
}

.header-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a202c;
  letter-spacing: -0.01em;
}

/* ── Legend ── */
.legend-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.legend-swatch {
  width: 11px;
  height: 11px;
  border-radius: 2px;
  flex-shrink: 0;
}

.legend-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #6b7280;
  text-transform: uppercase;
}

/* ── Chart ── */
.chart-wrap {
  padding: 8px 12px 4px;
}

.chart-wrap ::v-deep .apexcharts-legend {
  display: none !important;
}

/* ── Tooltip ── */
.chart-wrap ::v-deep .heatmap-tooltip {
  padding: 8px 10px;
}
.chart-wrap ::v-deep .heatmap-tooltip .tt-title {
  font-weight: 700;
  font-size: 11px;
  margin-bottom: 4px;
  color: #1a202c;
}
.chart-wrap ::v-deep .heatmap-tooltip .tt-row {
  font-size: 11px;
  color: #4a5568;
  line-height: 1.5;
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 40px 20px 48px;
}
.empty-text {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}
.empty-subtext {
  font-size: 11px;
  color: #9aa5b4;
}
</style>
